import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { StatusBadge } from "@/components/data/status-badge";
import { useToast } from "@/hooks/use-toast";
import { useFuncionarios } from "@/hooks/use-equipe";
import {
  useFrentesManejo, useSalvarFrente, useAudienciasDaFrente, usePreviaLembrete, useEnviarLembreteAgora,
  type FrenteManejo, type FrenteInput,
} from "@/hooks/use-manejo";
import { ArrowDown, ArrowUp, Pencil, Plus } from "lucide-react";

// ⚙ Frentes de trabalho — dentro de Operações (não no menu Administração).
// Só quem tem `manejo.frentes.configurar` chega aqui (a página esconde o ícone).

const NENHUM = "nenhum";

export function FrentesConfig({ aberto, onOpenChange }: { aberto: boolean; onOpenChange: (a: boolean) => void }) {
  const { toast } = useToast();
  const { data: frentes = [] } = useFrentesManejo(true);
  const salvar = useSalvarFrente();
  const [editando, setEditando] = useState<FrenteManejo | "nova" | null>(null);

  const mover = async (indice: number, delta: -1 | 1) => {
    const a = frentes[indice];
    const b = frentes[indice + delta];
    if (!a || !b) return;
    try {
      // Troca as posições; `ordem` é reescrita nas duas frentes (valores distintos
      // garantidos mesmo quando as duas tinham a mesma ordem).
      const base = Math.min(a.ordem, b.ordem);
      await salvar.mutateAsync({ id: a.id, ordem: delta === -1 ? base : base + 1 });
      await salvar.mutateAsync({ id: b.id, ordem: delta === -1 ? base + 1 : base });
    } catch (e: any) {
      toast({ title: "Não deu para reordenar", description: e?.message, variant: "destructive" });
    }
  };

  return (
    <Sheet open={aberto} onOpenChange={onOpenChange}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-lg">
        <SheetHeader className="text-left">
          <SheetTitle>Frentes de trabalho</SheetTitle>
          <SheetDescription>Agrupam as pendências do Manejo e definem quem recebe o lembrete diário.</SheetDescription>
        </SheetHeader>

        {editando ? (
          <FormFrente
            key={editando === "nova" ? "nova" : editando.id}
            frente={editando === "nova" ? null : editando}
            proximaOrdem={frentes.reduce((m, f) => Math.max(m, f.ordem), 0) + 1}
            onVoltar={() => setEditando(null)}
          />
        ) : (
          <div className="space-y-3 py-4">
            <Button className="min-h-11 w-full" onClick={() => setEditando("nova")} data-testid="btn-nova-frente">
              <Plus className="h-4 w-4 mr-1.5" />Nova frente
            </Button>
            {frentes.length === 0 && (
              <p className="text-sm text-muted-foreground">Nenhuma frente ainda. Comece por &quot;Água e Irrigação&quot; ou &quot;Fazenda/Geral&quot;.</p>
            )}
            <ul className="divide-y rounded-md border">
              {frentes.map((f, i) => (
                <li key={f.id} className="flex items-center gap-1 px-3 py-2" data-testid={`frente-${f.id}`}>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium">
                      {f.emoji ? `${f.emoji} ` : ""}{f.nome}
                      {!f.ativo && <StatusBadge tom="neutro" className="ml-2">inativa</StatusBadge>}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {[f.responsavelNome && `Resp.: ${f.responsavelNome}`, f.encarregadoNome && `Enc.: ${f.encarregadoNome}`, f.audienciaNome]
                        .filter(Boolean).join(" · ") || "Sem responsável nem grupo"}
                    </p>
                  </div>
                  <Button variant="ghost" className="min-h-11 min-w-11 px-0" disabled={i === 0 || salvar.isPending} onClick={() => mover(i, -1)} aria-label="Subir"><ArrowUp className="h-4 w-4" /></Button>
                  <Button variant="ghost" className="min-h-11 min-w-11 px-0" disabled={i === frentes.length - 1 || salvar.isPending} onClick={() => mover(i, 1)} aria-label="Descer"><ArrowDown className="h-4 w-4" /></Button>
                  <Button variant="ghost" className="min-h-11 min-w-11 px-0" onClick={() => setEditando(f)} aria-label="Editar frente"><Pencil className="h-4 w-4" /></Button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function FormFrente({ frente, proximaOrdem, onVoltar }: { frente: FrenteManejo | null; proximaOrdem: number; onVoltar: () => void }) {
  const { toast } = useToast();
  const { data: funcionarios = [] } = useFuncionarios();
  const { data: audiencias = [] } = useAudienciasDaFrente();
  const salvar = useSalvarFrente();

  const [nome, setNome] = useState(frente?.nome ?? "");
  const [emoji, setEmoji] = useState(frente?.emoji ?? "");
  const [responsavel, setResponsavel] = useState(frente?.responsavelFuncionarioId ?? NENHUM);
  const [encarregado, setEncarregado] = useState(frente?.encarregadoFuncionarioId ?? NENHUM);
  const [grupo, setGrupo] = useState(frente?.agentAudienciaId ?? NENHUM);
  const [lembrete, setLembrete] = useState(frente?.lembreteAtivo ?? false);
  const [ativo, setAtivo] = useState(frente?.ativo ?? true);

  const nulo = (v: string) => (v === NENHUM ? null : v);

  const gravar = async () => {
    const dados: FrenteInput & { id?: string } = {
      id: frente?.id,
      nome: nome.trim(),
      emoji: emoji.trim() ? emoji.trim() : null,
      responsavelFuncionarioId: nulo(responsavel),
      encarregadoFuncionarioId: nulo(encarregado),
      agentAudienciaId: nulo(grupo),
      lembreteAtivo: lembrete,
      ativo,
    };
    if (!frente) dados.ordem = proximaOrdem;
    try {
      await salvar.mutateAsync(dados);
      toast({ title: frente ? "Frente atualizada" : "Frente criada" });
      onVoltar();
    } catch (e: any) {
      toast({ title: "Não deu para salvar", description: e?.message, variant: "destructive" });
    }
  };

  const seletor = (valor: string, onChange: (v: string) => void, vazio: string, opcoes: Array<{ id: string; rotulo: string }>) => (
    <Select value={valor} onValueChange={onChange}>
      <SelectTrigger className="min-h-11"><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value={NENHUM}>{vazio}</SelectItem>
        {opcoes.map(o => <SelectItem key={o.id} value={o.id}>{o.rotulo}</SelectItem>)}
      </SelectContent>
    </Select>
  );
  const pessoas = funcionarios.map(f => ({ id: f.id, rotulo: f.apelido || f.nome }));

  return (
    <div className="space-y-4 py-4">
      <div className="grid grid-cols-[1fr_5rem] gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="fr-nome">Nome</Label>
          <Input id="fr-nome" value={nome} onChange={e => setNome(e.target.value)} className="min-h-11" placeholder="Água e Irrigação" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="fr-emoji">Emoji</Label>
          <Input id="fr-emoji" value={emoji} onChange={e => setEmoji(e.target.value)} className="min-h-11 text-center" placeholder="🚧" maxLength={8} />
        </div>
      </div>
      <div className="space-y-1.5">
        <Label>Responsável padrão</Label>
        {seletor(responsavel, setResponsavel, "Ninguém", pessoas)}
        <p className="text-xs text-muted-foreground">Quem fica atribuído aos itens novos da frente.</p>
      </div>
      <div className="space-y-1.5">
        <Label>Encarregado</Label>
        {seletor(encarregado, setEncarregado, "Ninguém", pessoas)}
      </div>
      <div className="space-y-1.5">
        <Label>Grupo do WhatsApp</Label>
        {seletor(grupo, setGrupo, "Sem grupo", audiencias.map(a => ({ id: a.id, rotulo: a.nome })))}
        <p className="text-xs text-muted-foreground">O lembrete diário usa este grupo.</p>
      </div>
      <div className="flex items-start justify-between gap-3 rounded-md border p-3">
        <div>
          <Label htmlFor="fr-lembrete">Lembrete ativo</Label>
          <p className="text-xs text-muted-foreground">Seg–sex, a partir das 06:30, a lista da frente vai para o grupo. Precisa de um grupo escolhido.</p>
        </div>
        <Switch id="fr-lembrete" checked={lembrete} onCheckedChange={setLembrete} />
      </div>
      <div className="flex items-center justify-between gap-3 rounded-md border p-3">
        <Label htmlFor="fr-ativo">Frente ativa</Label>
        <Switch id="fr-ativo" checked={ativo} onCheckedChange={setAtivo} />
      </div>
      {frente && <LembreteDaFrente frente={frente} />}
      <div className="flex gap-2">
        <Button variant="outline" className="min-h-11 flex-1" onClick={onVoltar}>Voltar</Button>
        <Button className="min-h-11 flex-1" disabled={!nome.trim() || salvar.isPending} onClick={gravar} data-testid="btn-salvar-frente">Salvar</Button>
      </div>
    </div>
  );
}

/** Prévia da mensagem de hoje + "Enviar agora" (confirmado) + último envio. */
function LembreteDaFrente({ frente }: { frente: FrenteManejo }) {
  const { toast } = useToast();
  const [aberta, setAberta] = useState(false);
  const { data: previa, isFetching, refetch } = usePreviaLembrete(frente.id, aberta);
  const enviar = useEnviarLembreteAgora();

  const enviarAgora = async () => {
    if (!frente.audienciaNome) {
      toast({ title: "Escolha o grupo do WhatsApp", description: "Salve a frente com um grupo antes de enviar.", variant: "destructive" });
      return;
    }
    if (!window.confirm(`Enviar para o grupo ${frente.audienciaNome} agora?`)) return;
    try {
      const r = await enviar.mutateAsync(frente.id);
      toast({ title: r.desfecho === "pulado_vazio" ? "Nada a enviar: a frente está vazia" : "Lembrete enviado" });
      refetch();
    } catch (e: any) {
      toast({ title: "Não deu para enviar", description: e?.message, variant: "destructive" });
    }
  };

  const ultimo = previa?.ultimoEnvio;
  return (
    <div className="space-y-2 rounded-md border p-3" data-testid="lembrete-frente">
      <p className="text-sm font-medium">Lembrete de hoje</p>
      <div className="flex gap-2">
        <Button variant="outline" className="min-h-11 flex-1" onClick={() => (aberta ? refetch() : setAberta(true))} disabled={isFetching}>
          {aberta ? "Atualizar prévia" : "Ver prévia"}
        </Button>
        <Button className="min-h-11 flex-1" onClick={enviarAgora} disabled={enviar.isPending} data-testid="btn-enviar-agora">
          Enviar agora
        </Button>
      </div>
      {aberta && previa && (
        <>
          {previa.vazio
            ? <p className="text-sm text-muted-foreground">Frente sem nada aberto nem feito: o lembrete não seria enviado.</p>
            : <pre className="whitespace-pre-wrap break-words rounded-md bg-muted/40 p-3 text-sm font-sans" data-testid="previa-texto">{previa.texto}</pre>}
          <p className="text-xs text-muted-foreground">
            {ultimo
              ? `Último envio: ${new Date(ultimo.em).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })} · ${ultimo.status}${ultimo.manual ? " (manual)" : ""}`
              : "Nenhum envio ainda."}
          </p>
        </>
      )}
    </div>
  );
}
